import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  BuildingOfficeIcon,
  ServerStackIcon,
  DevicePhoneMobileIcon,
  WifiIcon,
  UserIcon,
  MagnifyingGlassIcon,
  Bars3Icon,
  XMarkIcon,
  ClockIcon,
  CogIcon,
  KeyIcon,
  ArrowsRightLeftIcon
} from '@heroicons/react/24/outline';
import { useManagement } from '../context/ManagementContext';
import LanguageSwitcher from './LanguageSwitcher';
import Header from './Header';
import { useTranslation } from 'react-i18next';

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  external?: boolean;
}

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const location = useLocation();
  const { state, dispatch } = useManagement();

  const navigation: NavigationItem[] = [
    { name: t('common.dashboard', { defaultValue: 'Dashboard' }), href: '/management', icon: HomeIcon },
    // { name: t('common.hierarchy', { defaultValue: 'Hierarchy' }), href: '/management/hierarchy', icon: ChartBarIcon },
    // { name: 'Charts Demo', href: '/demo', icon: PresentationChartLineIcon, external: true },
    { name: t('customer.title'), href: '/management/customers', icon: BuildingOfficeIcon },
    { name: t('asset.title'), href: '/management/assets', icon: ServerStackIcon },
    { name: t('device.title'), href: '/management/devices', icon: DevicePhoneMobileIcon },
    { name: t('gateway.title'), href: '/management/gateways', icon: WifiIcon },
    { name: 'Users', href: '/management/users', icon: UserIcon },
    // { name: 'Alerts', href: '/management/alerts', icon: ExclamationTriangleIcon },
    { name: 'Backfill Tool', href: '/management/backfill', icon: ArrowsRightLeftIcon },
    { name: 'API Clients', href: '/management/api-clients', icon: KeyIcon },
    { name: 'Refresh Jobs', href: '/management/refresh-jobs', icon: ClockIcon },
    { name: 'System Settings', href: '/management/settings', icon: CogIcon },
  ];

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'SET_SEARCH_TERM', payload: e.target.value });
  };

  const isActiveRoute = (href: string) => {
    if (href === '/management') {
      return location.pathname === '/management' || location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-900/50" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 w-72 bg-white shadow-xl">
          <div className="flex h-16 items-center justify-between px-4">
            <div className="flex items-center">
              <WifiIcon className="h-8 w-8 text-blue-600" />
              <span className="ml-2 text-lg font-bold text-gray-900 leading-tight">
                {t('common.energyManagement', { defaultValue: 'Energy Management' })}
              </span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <nav className="mt-5 px-2">
            {navigation.map((item) => (
              item.external ? (
                <a
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-700 hover:bg-gray-50 hover:text-gray-900 group flex items-center px-3 py-3 text-base font-medium rounded-md mb-1 min-h-[52px]"
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="text-gray-400 group-hover:text-gray-500 mr-3 h-5 w-5 flex-shrink-0" />
                  <span className="leading-tight text-left">{item.name}</span>
                </a>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`${isActiveRoute(item.href)
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    } group flex items-center px-3 py-3 text-base font-medium rounded-md mb-1 min-h-[52px]`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon
                    className={`${isActiveRoute(item.href) ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
                      } mr-3 h-5 w-5 flex-shrink-0`}
                  />
                  <span className="leading-tight text-left">{item.name}</span>
                </Link>
              )
            ))}
          </nav>
        </div>
      </div>

      {/* Static sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col">
        <div className="flex min-h-0 flex-1 flex-col bg-white shadow-lg">
          <div className="flex h-16 items-center px-4">
            <WifiIcon className="h-8 w-8 text-blue-600" />
            <span className="ml-2 text-lg font-bold text-gray-900 leading-tight">{t('common.energyManagement', { defaultValue: 'Energy Management' })}</span>
          </div>
          <nav className="mt-5 flex-1 px-2 pb-4">
            {navigation.map((item) => (
              item.external ? (
                <a
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-700 hover:bg-gray-50 hover:text-gray-900 group flex items-center px-3 py-3 text-sm font-medium rounded-md mb-1 min-h-[48px]"
                >
                  <item.icon className="text-gray-400 group-hover:text-gray-500 mr-3 h-5 w-5 flex-shrink-0" />
                  <span className="leading-tight text-left">{item.name}</span>
                </a>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`${isActiveRoute(item.href)
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    } group flex items-center px-3 py-3 text-sm font-medium rounded-md mb-1 min-h-[48px]`}
                >
                  <item.icon
                    className={`${isActiveRoute(item.href) ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
                      } mr-3 h-5 w-5 flex-shrink-0`}
                  />
                  <span className="leading-tight text-left">{item.name}</span>
                </Link>
              )
            ))}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Top header */}
        <div className="sticky top-0 z-40">
          <Header />
        </div>

        {/* Page header with mobile menu and search */}
        <div className="bg-white shadow-sm">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden -ml-0.5 -mt-0.5 inline-flex h-12 w-12 items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
              <div className="ml-4 lg:ml-0">
                <h1 className="text-lg font-semibold text-gray-900">
                  {navigation.find(item => isActiveRoute(item.href))?.name || 'Dashboard'}
                </h1>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Search */}
              <div className="hidden md:block">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute inset-y-0 left-0 flex h-full w-5 items-center pl-3 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t('common.searchEntities', { defaultValue: 'Search entities...' })}
                    value={state.searchTerm}
                    onChange={handleSearch}
                    className="block w-full rounded-md border-0 bg-gray-50 py-2 pl-10 pr-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:bg-white focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                  />
                </div>
              </div>

              {/* Mobile search toggle */}
              <button
                onClick={() => setShowMobileSearch(!showMobileSearch)}
                className="md:hidden rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500"
              >
                <MagnifyingGlassIcon className="h-6 w-6" />
              </button>

              {/* Language Switcher */}
              <LanguageSwitcher />

              {/* Status indicator */}
              <div className="flex items-center space-x-1">
                <div className={`h-2 w-2 rounded-full ${state.loading ? 'bg-yellow-400' : state.error ? 'bg-red-400' : 'bg-green-400'}`} />
                <span className="text-sm text-gray-500">
                  {state.loading ? t('common.loading') : state.error ? t('common.error') : t('common.connected', { defaultValue: 'Connected' })}
                </span>
              </div>
            </div>
          </div>

          {/* Mobile search */}
          {showMobileSearch && (
            <div className="md:hidden px-4 pb-2">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute inset-y-0 left-0 flex h-full w-5 items-center pl-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search entities..."
                  value={state.searchTerm}
                  onChange={handleSearch}
                  className="block w-full rounded-md border-0 bg-gray-50 py-2 pl-10 pr-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:bg-white focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                />
              </div>
            </div>
          )}
        </div>

        {/* Page content */}
        <main className="py-8">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
